import React from "react";
import { Link } from "react-router-dom";
import { Section, SerifH, Eyebrow, PrimaryButton, SecondaryButton, Bullet, Card } from "../components/ui-kit/Primitives";
import { ArrowUpRight, Sprout, Building2, HeartHandshake, Compass, ShieldCheck, Users } from "lucide-react";
import { PAGE_IMAGES } from "../lib/images";

const heroImg = PAGE_IMAGES.about; // Black woman professional portrait
const trustImg = PAGE_IMAGES.homePeer;
const consultingImg = PAGE_IMAGES.homeConsulting;

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative pt-16 pb-24 sm:pt-24 sm:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <Eyebrow>Peer Support · Community Wellness · Raleigh, NC</Eyebrow>
            <SerifH as="h1" className="text-5xl sm:text-6xl lg:text-7xl">
              Peer support that <em className="not-italic text-brand-gold">walks</em> beside you —
              <br className="hidden sm:block" />on the road to wellness.
            </SerifH>
            <p className="mt-7 text-lg sm:text-xl text-slate-600 font-light max-w-2xl leading-relaxed">
              KBX Pathways To Wellness is a peer support company offering one-on-one and community-based
              support rooted in lived experience. We help individuals and families navigate recovery,
              mental wellness, life transitions, and the path back to stability.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <PrimaryButton to="/peer-support" testid="hero-explore-peer-support">Explore Peer Support</PrimaryButton>
              <SecondaryButton to="/contact" testid="hero-book-consultation">Book a Consultation</SecondaryButton>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs uppercase tracking-[0.22em] text-slate-500">
              <span>Lived Experience</span>
              <span className="w-1 h-1 rounded-full bg-brand-sage" />
              <span>Recovery &amp; Wellness</span>
              <span className="w-1 h-1 rounded-full bg-brand-sage" />
              <span>Community-Based Support</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative animate-fade-up delay-200">
            <div className="relative rounded-[2rem] overflow-hidden border border-brand-green/15 shadow-xl">
              <img src={heroImg} alt="Diverse community supporting each other" className="w-full h-[560px] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur rounded-2xl p-5 border border-brand-green/10">
                <div className="text-xs tracking-[0.2em] uppercase text-brand-sage">Our Mission</div>
                <p className="font-serif text-xl text-brand-green leading-snug mt-1">
                  Where lived experience, wellness, and business structure meet.
                </p>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-brand-gold/20 -z-10" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-brand-sage/30 -z-10" />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Section data-testid="trust-section" className="bg-brand-mist/60">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Eyebrow>Why KBX</Eyebrow>
            <SerifH className="text-4xl sm:text-5xl">
              Structure for the work that matters most.
            </SerifH>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-slate-700 font-light leading-relaxed">
              KBX Pathways To Wellness supports agencies and founders with the structure needed to
              launch, operate, and grow in the peer support and behavioral health space. Our work
              focuses on business development, compliance readiness, operational systems, client
              experience, community impact, and long-term sustainability.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10">
              {[
                { icon: ShieldCheck, label: "Compliance Readiness" },
                { icon: Building2, label: "Agency Structure" },
                { icon: Users, label: "Community Impact" },
                { icon: Compass, label: "Long-term Strategy" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-start gap-2">
                  <div className="w-11 h-11 rounded-xl bg-white border border-brand-green/15 flex items-center justify-center text-brand-green">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-sm text-slate-700 leading-tight">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Services Snapshot */}
      <Section data-testid="services-snapshot">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Eyebrow>A Peer Support Company — First.</Eyebrow>
          <SerifH className="text-4xl sm:text-5xl">Community-based peer support, with consulting that strengthens the field.</SerifH>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Peer Support — PRIMARY */}
          <Card className="relative overflow-hidden p-0">
            <img src={trustImg} alt="Peer support group" className="w-full h-56 object-cover" />
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green">
                  <HeartHandshake className="w-5 h-5" />
                </div>
                <Eyebrow color="gold">Our Primary Service</Eyebrow>
              </div>
              <SerifH as="h3" className="text-3xl">Peer Support &amp; Community Wellness</SerifH>
              <p className="mt-4 text-slate-600 leading-relaxed">
                At our core, KBX is a peer support company. We walk beside individuals and families
                navigating recovery, mental wellness, life transitions, and community reintegration —
                with support rooted in lived experience, hope, and accountability.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "One-on-one peer support",
                  "Recovery support",
                  "Mental & emotional wellness",
                  "Substance use recovery",
                  "Life skills development",
                  "Community resource navigation",
                  "Goal planning & advocacy",
                  "Family & re-entry support",
                ].map((x) => (
                  <Bullet key={x}>{x}</Bullet>
                ))}
              </ul>
              <Link
                to="/peer-support"
                data-testid="services-peer-link"
                className="mt-8 inline-flex items-center gap-2 text-brand-green font-medium hover:gap-3 transition-all"
              >
                Explore peer support services <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Card>

          {/* Consulting — SECONDARY */}
          <Card className="relative overflow-hidden p-0">
            <img src={consultingImg} alt="Consulting team" className="w-full h-56 object-cover" />
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-brand-gold/15 flex items-center justify-center text-brand-gold">
                  <Building2 className="w-5 h-5" />
                </div>
                <Eyebrow color="gold">Also Offered</Eyebrow>
              </div>
              <SerifH as="h3" className="text-3xl">Consulting for Peer Support Agencies</SerifH>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Secondarily, we help peer support agencies and behavioral health entrepreneurs
                build, structure, and grow with confidence.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "Business formation",
                  "Program structure",
                  "Policy & procedures",
                  "NCTracks / Medicaid prep",
                  "Credentialing readiness",
                  "Documentation workflows",
                  "Staffing & roles",
                  "Funding & growth strategy",
                ].map((x) => (
                  <Bullet key={x}>{x}</Bullet>
                ))}
              </ul>
              <Link
                to="/consulting"
                data-testid="services-consulting-link"
                className="mt-8 inline-flex items-center gap-2 text-brand-green font-medium hover:gap-3 transition-all"
              >
                View consulting services <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Card>
        </div>
      </Section>

      {/* Pillars / Approach Strip */}
      <Section data-testid="approach-strip" className="bg-brand-green text-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Eyebrow color="gold">Our Approach</Eyebrow>
            <SerifH className="text-4xl sm:text-5xl text-white">From idea to implementation — with clarity, strategy, and accountability.</SerifH>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Discover", d: "We listen and map your agency's strengths, gaps, and goals." },
              { n: "02", t: "Structure", d: "We design programs, systems, and compliance frameworks." },
              { n: "03", t: "Sustain", d: "We position you for funding, partnerships, and long-term growth." },
            ].map((s) => (
              <div key={s.n} className="border-t border-white/15 pt-6">
                <div className="text-brand-gold text-sm tracking-[0.2em]">{s.n}</div>
                <div className="font-serif text-2xl mt-2">{s.t}</div>
                <p className="text-slate-300 text-sm mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Closing CTA */}
      <Section data-testid="closing-cta">
        <div className="relative rounded-[2rem] overflow-hidden bg-brand-navy text-white p-10 sm:p-16">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-gold/15 blur-2xl" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-brand-sage/15 blur-2xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Eyebrow color="gold">Ready when you are</Eyebrow>
              <SerifH className="text-4xl sm:text-5xl text-white">
                Ready to build a stronger pathway forward?
              </SerifH>
              <p className="mt-5 text-slate-300 text-lg font-light max-w-2xl leading-relaxed">
                Whether you are launching a peer support agency, restructuring your current organization,
                or seeking support for your wellness journey, KBX is here to help.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/contact"
                data-testid="closing-cta-schedule"
                className="inline-flex items-center gap-2 bg-brand-gold text-brand-navy rounded-full px-7 py-4 font-medium hover:bg-white transition-colors"
              >
                Schedule a Consultation <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
